"use client";
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { handleError } from "@/utils/errorHandler";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import TokenRefreshService from "@/services/TokenRefreshService";
import { analytics } from "@/lib/analytics";
import { getRole } from "@/service/jwtService";

interface User {
  sub?: string;
  roles?: string[];
  ipv?: boolean;
  irf?: boolean;
  given_name?: string;
  // Add other properties as needed
}

// The same shape, named for use from the server side, which resolves this from
// the access token and hands it back in as initialUser.
export type AuthUser = User;

interface AuthContextType {
  isAuthenticated: () => boolean;
  user: User | null;
  loading: boolean;
  updateAuth: (userData: User | null) => void;
  updatePhoneVerification: () => void;
  refreshAuthState: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: () => false,
  user: null,
  loading: true,
  updateAuth: () => {},
  updatePhoneVerification: () => {},
  refreshAuthState: async () => {},
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
  // Resolved from the access token while the page renders, so the first paint
  // already knows who you are. Null when the server could not read the token
  // (absent, or expired and in need of a refresh it cannot perform), in which
  // case the /api/status round trip below still runs.
  initialUser?: User | null;
}> = ({ children, initialUser = null }) => {
  const [user, setUser] = useState<User | null>(initialUser);
  const [loading, setLoading] = useState(initialUser === null);

  const updateAuth = useCallback((userData: User | null) => {
    setUser(userData);
    if (userData?.sub) {
      const role = userData.roles ? getRole(userData.roles) : undefined;
      analytics.identify(userData.sub, { role });
    }
  }, []);

  const refreshAuthState = useCallback(async () => {
    try {
      const response = await fetch("/api/status");
      if (response.ok) {
        const data = await response.json();
        updateAuth(data.user);
      } else {
        handleError(new Error(`Failed to refresh auth state: ${response.status} ${response.statusText}`), {
          component: "AuthContext",
          action: "refreshAuthState"
        });
      }
    } catch (error) {
      handleError(error, { component: "AuthContext", action: "refreshAuthState" });
    }
  }, [updateAuth]);

  const logout = useCallback(async () => {
    try {
      TokenRefreshService.getInstance().stop();
      const response = await fetch("/api/logout", { method: "POST" });
      if (response.ok) {
        analytics.reset();
        updateAuth(null);
      } else {
        const errorText = await response.text();
        handleError(new Error(`Logout failed: ${errorText}`), { component: "AuthContext", action: "logout" });
      }
    } catch (error) {
      handleError(error, { component: "AuthContext", action: "logout" });
    }
  }, [updateAuth]);

  const updatePhoneVerification = useCallback(() => {
    setUser((current) => (current ? { ...current, ipv: true } : current));
  }, []);

  const isAuthenticated = useCallback(() => user != null, [user]);

  const handleTokenRefresh = useCallback(async () => {
    await refreshAuthState();
  }, [refreshAuthState]);

  useTokenRefresh(handleTokenRefresh, isAuthenticated());

  useEffect(() => {
    const checkAuth = async () => {
      // Seeded from the server: the answer is already here, and asking again
      // would only re-run the check that produced it.
      if (user === null) {
        try {
          const response = await fetch("/api/status");

          if (!response.ok) {
            // Authentication failed, logout to clean up state
            await logout();
            return;
          }

          const data = await response.json();
          updateAuth(data.user);
        } catch (error) {
          handleError(error, { component: "AuthContext", action: "checkAuthStatus" });
          await logout();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Consumers read this object on every render, and several of them key
  // effects off what it holds. Rebuilding it each time re-ran those effects,
  // which is what made the nav bar refetch its venues on unrelated renders.
  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      loading,
      updateAuth,
      updatePhoneVerification,
      refreshAuthState,
      logout,
    }),
    [
      isAuthenticated,
      user,
      loading,
      updateAuth,
      updatePhoneVerification,
      refreshAuthState,
      logout,
    ]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};